import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { AlertTriangle, TrendingDown, TrendingUp, Check, ArrowRight, ChevronDown, ChevronUp, Play, Sparkles, Target, Zap, Phone, MessageCircle, BookOpen, Clock, Star, Lock } from 'lucide-react';
import useAbVariant from '../lib/useAbVariant';
import AnimatedAvatar from '../components/AnimatedAvatar';
import SabrilCloserBubble from '../components/SabrilCloserBubble';
import FranceClosureMap from '../components/FranceClosureMap';
import PhilosophyGate from '../components/PhilosophyGate';
import PremiumDarkCard, { MetricTile } from '../components/PremiumDarkCard';
import AuditRealizationSection from '../components/AuditRealizationSection';
import ResultsTensionGate from '../components/ResultsTensionGate';
import NarrativeChoiceGate from '../components/NarrativeChoiceGate';
import ResultsStorytellingBlock from '../components/ResultsStorytellingBlock';
import OffersQuickAccess from '../components/OffersQuickAccess';
import SabrilPixarAvatar, { SabrilSpeechBubble } from '../components/SabrilPixarAvatar';
import { saveUserContext } from '../lib/userContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WhatsApp Business number and link
const WHATSAPP_NUMBER = "33745522450";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Salut%20je%20viens%20de%20faire%20l'audit%20et%20je%20veux%20en%20parler`;

// ============ THEME ============
const theme = {
  bg: '#f5f3f0',
  card: '#ffffff',
  border: '#e0dbd4',
  text: '#1a1a1a',
  textMuted: 'rgba(26,26,26,0.6)',
  textLight: 'rgba(26,26,26,0.4)',
  orange: '#F05A1A',
  green: '#22C55E',
  red: '#EF4444',
  yellow: '#F59E0B',
  indigo: '#6366F1',
};

// ============ FAQ ACCORDION ============
const FAQAccordion = ({ items }) => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={i} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setOpenIdx(isOpen ? null : i)}
              className="w-full px-4 py-3.5 flex items-start gap-3 text-left hover:bg-gray-100 transition"
              data-testid={`faq-toggle-${i}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition ${isOpen ? 'bg-orange-500 text-white' : 'bg-white border border-gray-300 text-gray-500'}`}>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </div>
              <span className="flex-1 text-gray-900 font-bold text-sm leading-snug">{item.q}</span>
            </button>
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 pt-0 text-gray-700 text-sm leading-relaxed pl-13">
                    {item.a}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

// ============ STICKY MOBILE CTA ============
const StickyMobileCTA = ({ onClick }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const ctaEl = document.getElementById('offres-cta-anchor');
      if (!ctaEl) {
        setVisible(window.scrollY > 600);
        return;
      }
      const rect = ctaEl.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight && rect.bottom > 0;
      setVisible(window.scrollY > 600 && !isInView);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="md:hidden fixed bottom-0 left-0 right-0 z-50 p-3 bg-white/95 backdrop-blur-sm border-t border-gray-200"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0))' }}
        >
          <button
            onClick={onClick}
            data-testid="offres-sticky-mobile-cta"
            className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-black text-sm rounded-xl flex items-center justify-center gap-2 transition shadow-lg"
            style={{ boxShadow: '0 8px 24px rgba(240,90,26,0.4)' }}
          >
            Découvrir les offres
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============ TWO OFFERS SECTION (remplace l'ancien ebook 47€) ============
const TwoOffersSection = ({ caValue, margePerdueParAn }) => {
  const navigate = useNavigate();
  const goToSabrilExpress = () => {
    try { window.__abTrack?.('cta_click', { offer: 'sabril_express' }); } catch (_e) { /* ignore */ }
    // Redirige vers la carte Sabril Express sur la page de vente
    navigate('/prsnl#offres');
    setTimeout(() => {
      const el = document.getElementById('offres');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };
  const goToConfigurateur = () => {
    try { window.__abTrack?.('cta_click', { offer: 'salon_prive' }); } catch (_e) { /* ignore */ }
    navigate('/configurer');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden"
    >
      {/* Perte quotidienne — loss aversion (conservée) */}
      {margePerdueParAn > 0 && (
        <div className="mb-5 p-4 bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl">
          <p className="text-red-700 text-[10px] font-black uppercase tracking-widest mb-1">Chaque jour d&apos;attente</p>
          <p className="text-gray-900 text-base font-bold leading-snug">
            Chaque jour sans agir =
            <span className="text-red-600 text-2xl font-black tabular-nums ml-2">
              −{Math.round(margePerdueParAn / 365).toLocaleString('fr-FR')}€
            </span>
          </p>
          <p className="text-gray-600 text-xs mt-1">
            Soit <span className="font-bold tabular-nums">−{Math.round(margePerdueParAn / 52).toLocaleString('fr-FR')}€</span> cette semaine.
          </p>
        </div>
      )}

      {/* Anchor pour le sticky CTA */}
      <div id="offres-cta-anchor" />

      {/* ═══════════ OFFRE 1 · SABRIL EXPRESS 790€ ═══════════ */}
      <div className="bg-white rounded-3xl p-6 border border-gray-200 mb-5 relative overflow-hidden" style={{ boxShadow: '0 12px 40px rgba(240,90,26,0.10)' }}>
        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-500 text-white text-[9px] font-black uppercase tracking-widest rounded-full mb-3">
          Étape 1 · Sabril Express
        </div>
        <h3 className="text-xl font-black text-gray-900 leading-[1.1] tracking-tight mb-2">
          Deux visios avec Sabril +<br/>
          <span className="text-orange-500">votre plan d&apos;action personnalisé.</span>
        </h3>
        <p className="text-gray-600 text-sm leading-snug mb-4">
          Vous obtenez rapidement un diagnostic tranché, un plan d&apos;action chiffré et 2 visios de 45 min pour cadrer vos priorités.
        </p>
        <div className="mb-4 rounded-2xl p-4 border border-gray-200 bg-gradient-to-br from-gray-50 to-white">
          <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-3">Ce que vous obtenez</p>
          <div className="space-y-2">
            {[
              { t: 'Visio 1 · Cadrage & priorités', d: '45 min — on relit vos chiffres ensemble et on définit ce qui doit bouger en premier.' },
              { t: 'Plan d\'action personnalisé (PDF)', d: 'Basé sur les réponses de votre audit — livré sous 72h.' },
              { t: 'Visio 2 · Suivi & ajustements', d: '45 min — 2 semaines plus tard, on ajuste le plan avec ce que vous avez déjà lancé.' },
              { t: 'WhatsApp direct pendant 30 jours', d: 'Une question pratique, une hésitation — vous m\'écrivez, je vous réponds.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 text-sm font-bold leading-tight">{item.t}</p>
                  <p className="text-gray-500 text-[11px] leading-tight mt-0.5">{item.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-baseline justify-between mb-4 pt-3 border-t border-gray-100">
          <div>
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Investissement</p>
            <p className="text-3xl font-black text-gray-900 tracking-tight tabular-nums">790&nbsp;€ <span className="text-sm text-gray-500 font-normal">HT</span></p>
          </div>
          <p className="text-gray-400 text-[11px] text-right leading-snug">Une fois<br/>Sans engagement</p>
        </div>
        <motion.button
          onClick={goToSabrilExpress}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-black text-base rounded-xl flex items-center justify-center gap-2 transition"
          style={{ boxShadow: '0 8px 24px rgba(240,90,26,0.4)' }}
          data-testid="offres-sabril-express-btn"
        >
          Voir Sabril Express — 790 €
          <ArrowRight className="w-4 h-4" />
        </motion.button>
      </div>

      {/* ═══════════ OFFRE 2 · SALON PRIVÉ 90J ═══════════ */}
      <div className="bg-gradient-to-br from-[#0B2B24] to-[#0F3A31] text-white rounded-3xl p-6 border border-amber-400/30 relative overflow-hidden mb-5" style={{ boxShadow: '0 16px 44px rgba(11,43,36,0.35)' }}>
        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-400 text-[#0B2B24] text-[9px] font-black uppercase tracking-widest rounded-full mb-3">
          Étape 2 · Salon Privé
        </div>
        <h3 className="text-xl font-black leading-[1.1] tracking-tight mb-2">
          L&apos;accompagnement 90 jours
          <br/>
          <span className="text-amber-400">avec Sabril en direct.</span>
        </h3>
        <p className="text-white/70 text-sm leading-snug mb-4">
          Un programme complet, chiffré, livré. Nous ne prenons que quelques dirigeants par trimestre — pour bien faire notre travail.
        </p>
        <div className="mb-4 rounded-2xl p-4 border border-amber-400/25 bg-white/5">
          <p className="text-amber-400 text-[10px] font-black uppercase tracking-widest mb-3">3 formats disponibles</p>
          <div className="space-y-2">
            {[
              { t: '100 % à distance', d: 'Dès 4 990 € HT · 3 mois de coaching en visio + WhatsApp direct.' },
              { t: '48h express sur site', d: 'Dès 2 790 € HT · 2 jours d\'immersion + rapport d\'audit signé.' },
              { t: 'Accompagnement 90 jours', d: '24 990 € HT tout inclus · 15 jours terrain + 75 jours de suivi.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5">
                <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold leading-tight">{item.t}</p>
                  <p className="text-white/60 text-[11px] leading-tight mt-0.5">{item.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <motion.button
          onClick={goToConfigurateur}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-4 bg-amber-400 hover:bg-amber-300 text-[#0B2B24] font-black text-base rounded-xl flex items-center justify-center gap-2 transition"
          style={{ boxShadow: '0 8px 24px rgba(216,148,64,0.45)' }}
          data-testid="offres-salon-prive-btn"
        >
          Configurer mon accompagnement
          <ArrowRight className="w-4 h-4" />
        </motion.button>
      </div>

      {/* Rassurance & clôture */}
      <div className="mt-2 pt-5 border-t border-gray-200 text-center">
        <p className="text-gray-900 font-black text-sm">
          Vous travaillez pour votre resto.
        </p>
        <p className="text-orange-500 font-black text-base mt-1">
          Il est temps que votre resto travaille pour vous.
        </p>
      </div>

      {/* Sticky mobile CTA */}
      <StickyMobileCTA onClick={goToSabrilExpress} />
    </motion.div>
  );
};

// ============ ANIMATED COMPONENTS ============

// Animated Counter with easing
const AnimatedCounter = ({ value, suffix = "", prefix = "", duration = 1500 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView && value > 0) {
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);
  
  return <span ref={ref}>{prefix}{count.toLocaleString('fr-FR')}{suffix}</span>;
};

// Interactive Slider for simulations
const SimulationSlider = ({ label, value, onChange, min, max, step = 1, unit = "%", color = theme.orange, description, dark = false }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  const trackBg = dark ? 'rgba(255,255,255,0.10)' : '#e0dbd4';
  const labelColor = dark ? 'text-white/85' : 'text-gray-700';
  const descColor = dark ? 'text-white/55' : 'text-gray-500';

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className={`text-sm font-medium ${labelColor}`}>{label}</span>
        <span className="text-2xl font-black" style={{ color, textShadow: dark ? `0 0 18px ${color}66` : 'none' }}>{value}{unit}</span>
      </div>
      {description && <p className={`text-xs ${descColor}`}>{description}</p>}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-3 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${color} 0%, ${color} ${percentage}%, ${trackBg} ${percentage}%, ${trackBg} 100%)`,
          }}
        />
        <style>{`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: white;
            border: 4px solid ${color};
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            transition: transform 0.15s;
          }
          input[type="range"]::-webkit-slider-thumb:hover {
            transform: scale(1.1);
          }
          input[type="range"]::-moz-range-thumb {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: white;
            border: 4px solid ${color};
            cursor: pointer;
          }
        `}</style>
      </div>
      <div className="flex justify-between text-xs text-gray-400">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

// Animated Progress Ring
const ProgressRing = ({ percentage, size = 120, strokeWidth = 10, color = theme.orange }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  return (
    <div ref={ref} className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e0dbd4"
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={isInView ? { strokeDashoffset: offset } : {}}
          transition={{ duration: 1.5, ease: "easeOut" }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-3xl font-black" style={{ color }}>{percentage}%</span>
      </div>
    </div>
  );
};

// Comparison Table Row
// Sector benchmarks — for credibility and "vous vous dites : il se base sur quoi ?"
// Sources : observations marché Maison Avocado + référentiels GIRA, IFOP, KPMG resto.
const NICHE_BENCHMARKS = {
  fastfood:     { label: 'Fast-food',         food: 28, ms: 28, prime: 56, loyer: 8 },
  streetfood:   { label: 'Street food',       food: 26, ms: 26, prime: 52, loyer: 7 },
  traditionnel: { label: 'Traditionnel',      food: 30, ms: 32, prime: 62, loyer: 10 },
  gastro:       { label: 'Gastronomique',     food: 33, ms: 38, prime: 71, loyer: 12 },
  monde:        { label: 'Cuisine du monde',  food: 30, ms: 30, prime: 60, loyer: 9 },
  healthy:      { label: 'Healthy-Végé',      food: 32, ms: 30, prime: 62, loyer: 10 },
  pizzeria:     { label: 'Pizzeria',          food: 25, ms: 26, prime: 51, loyer: 8 },
  autre:        { label: 'Restauration',      food: 30, ms: 30, prime: 60, loyer: 10 },
};
const getBenchmark = (restoType) => NICHE_BENCHMARKS[restoType] || NICHE_BENCHMARKS.autre;

const ComparisonRow = ({ label, current, optimal, unit = "%", delay = 0, niche, kind, dark = false }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const isGood = current <= optimal;
  const delta = current - optimal;

  const labelColor = dark ? 'text-white/85' : 'text-gray-700';
  const borderColor = dark ? 'border-white/10' : 'border-gray-100';
  const muted = dark ? 'text-white/55' : 'text-gray-500';
  const strong = dark ? 'text-white/85' : 'text-gray-700';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -20 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay, duration: 0.4 }}
      className={`py-3 border-b ${borderColor}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-sm font-semibold ${labelColor}`}>{label}</span>
        <div className="flex items-center gap-3">
          <span
            className={`text-2xl font-black tabular-nums ${isGood ? 'text-green-400' : 'text-red-400'}`}
            style={dark ? { textShadow: isGood ? '0 0 18px rgba(16,185,129,0.5)' : '0 0 18px rgba(239,68,68,0.5)' } : {}}
          >
            {current}{unit}
          </span>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            dark
              ? (isGood ? 'bg-green-500/20 border border-green-500/40' : 'bg-red-500/20 border border-red-500/40')
              : (isGood ? 'bg-green-100' : 'bg-red-100')
          }`}>
            {isGood ? (
              <Check className={`w-4 h-4 ${dark ? 'text-green-400' : 'text-green-600'}`} />
            ) : (
              <TrendingDown className={`w-4 h-4 ${dark ? 'text-red-400' : 'text-red-500'}`} />
            )}
          </div>
        </div>
      </div>
      {/* Benchmark context — credibility builder */}
      <div className={`flex items-baseline gap-1.5 text-[11px] leading-snug ${muted}`}>
        <span>📊 Benchmark</span>
        {niche && <span className={`font-semibold ${strong}`}>{niche}</span>}
        <span>:</span>
        <span className={`font-semibold ${strong}`}>{optimal}{unit}</span>
        {!isGood && (
          <span className={dark ? 'text-red-400 font-bold' : 'text-red-600 font-bold'}>
            · Vous êtes {delta > 0 ? `+${delta}` : delta} pts au-dessus
          </span>
        )}
        {isGood && (
          <span className={dark ? 'text-green-400 font-bold' : 'text-green-600 font-bold'}>
            · Dans la norme {delta < 0 ? `(${delta} pts mieux)` : ''}
          </span>
        )}
      </div>
      {kind === 'food' && !isGood && (
        <p className={`text-[11px] mt-1 leading-snug ${muted}`}>
          💡 Chaque point au-dessus = {Math.round((delta / 100) * 1000)}€/mois perdus pour 100k€ de CA. Récupérable en retravaillant les 3 plats avec le pire ratio.
        </p>
      )}
      {kind === 'ms' && !isGood && (
        <p className={`text-[11px] mt-1 leading-snug ${muted}`}>
          💡 {delta} pts de MS = planning à recaler sur vos 2h les moins rentables. Aucune réduction d&apos;équipe — juste de l&apos;optimisation.
        </p>
      )}
    </motion.div>
  );
};

// Interactive Card with hover effect
const InteractiveCard = ({ children, className = "", onClick, highlighted = false }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -4 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`relative rounded-2xl p-6 backdrop-blur-sm overflow-hidden cursor-pointer transition-all border ${
      highlighted ? 'border-[#F05A1A]/50 ring-2 ring-[#F05A1A]/25' : 'border-white/10'
    } ${className}`}
    style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
  >
    {/* Atmospheric halo */}
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{
        background:
          'radial-gradient(circle at 20% 30%, rgba(240,90,26,0.18) 0%, transparent 55%), radial-gradient(circle at 80% 70%, rgba(239,68,68,0.10) 0%, transparent 55%)',
      }}
      aria-hidden="true"
    />
    <div className="relative">{children}</div>
  </motion.div>
);

// Expandable Section
const ExpandableSection = ({ title, icon, children, defaultOpen = false, color = theme.orange }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <span className="font-bold text-gray-800">{title}</span>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-5 h-5" style={{ color }} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 pb-6 border-t border-gray-100 pt-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Animated Stat Card
const StatCard = ({ icon, value, label, suffix = "", color = theme.orange, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.4 }}
      className="bg-white rounded-xl p-5 border border-gray-200 text-center"
    >
      <div className="text-3xl mb-2">{icon}</div>
      <div className="font-bebas text-4xl" style={{ color }}>
        <AnimatedCounter value={value} suffix={suffix} />
      </div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </motion.div>
  );
};

// ============ MAIN PAGE ============
const ResultsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [meta, setMeta] = useState({ mode: null, risk_score: null, readiness_score: null, diagnosis: null });
  const [simulatedFoodCost, setSimulatedFoodCost] = useState(30);
  const [simulatedMasseSalariale, setSimulatedMasseSalariale] = useState(30);
  const [showSimulator, setShowSimulator] = useState(false);
  const [philosophyDismissed, setPhilosophyDismissed] = useState(() => {
    try {
      return Boolean(localStorage.getItem(`prsnl_philo_seen_${id}`));
    } catch (_) {
      return false;
    }
  });
  const { variant: heroVariant, track: trackHero } = useAbVariant('results_hero_v1', { auditId: id });

  const handlePhilosophyDismiss = () => {
    try {
      localStorage.setItem(`prsnl_philo_seen_${id}`, '1');
    } catch (_) {}
    setPhilosophyDismissed(true);
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`${API_URL}/api/quiz/result/${id}`);
        if (response.ok) {
          const data = await response.json();
          const a = data.answers || {};
          setAnswers(a);
          setMeta({
            mode: data.mode || null,
            risk_score: data.risk_score ?? null,
            readiness_score: data.readiness_score ?? null,
            diagnosis: data.diagnosis || null,
          });
          setSimulatedFoodCost(a.food_cost || 30);
          setSimulatedMasseSalariale(a.masse_salariale || 30);
          // Persist user context for cross-page personalization (e.g. /prsnl)
          saveUserContext({
            auditId: id,
            firstName: a.first_name || a.firstName || null,
            restoName: a.resto_name || null,
            restoType: a.resto_type || null,
            projectStatus: a.project_status || null,
            ca: a.ca_mensuel || null,
            foodCost: a.food_cost || null,
            masseSalariale: a.masse_salariale || null,
            ticketMoyen: a.ticket_moyen || null,
            plusGrosProbleme: a.plus_gros_probleme || null,
            objectifPrincipal: a.objectif_principal || null,
            autonomie: a.autonomie || null,
            organisationSemaine: a.organisation_semaine || null,
          });
        }
      } catch (error) {
        console.error('Error fetching results:', error);
      }
      setLoading(false);
    };
    fetchResults();
  }, [id]);

  // Calculations
  const caValue = answers.ca_mensuel || 50000;
  const foodCost = answers.food_cost || 30;
  const masseSalariale = answers.masse_salariale || 30;
  const loyerMensuel = answers.loyer_mensuel || 3000;
  const primeCost = foodCost + masseSalariale;
  const unknownFoodCost = answers.connait_foodcost === 'non' || answers.connait_foodcost === 'sais_pas';
  
  // EBE Calculation (Excédent Brut d'Exploitation)
  // EBE = CA - Achats matières - Charges personnel - Loyer - Autres charges (estimées à 10%)
  const achatsMatieres = Math.round(caValue * (foodCost / 100));
  const chargesPersonnel = Math.round(caValue * (masseSalariale / 100));
  const autresCharges = Math.round(caValue * 0.10); // 10% pour autres charges
  const ebe = Math.round(caValue - achatsMatieres - chargesPersonnel - loyerMensuel - autresCharges);
  const ebePercent = Math.round((ebe / caValue) * 100);
  const ebeAnnuel = ebe * 12;
  
  // Current losses + OPPORTUNITY framing (never 0 — minimum floor of -500€/month)
  const optimalPrimeCost = 60;
  const excessPrimeCost = Math.max(0, primeCost - optimalPrimeCost);
  const rawMargePerdueParMois = Math.round((excessPrimeCost / 100) * caValue);
  // Floor: even if prime cost is optimal, there are missed opportunities (+20% CA, ticket moyen, upsell)
  const opportunityNonExploitee = Math.max(rawMargePerdueParMois, Math.round(caValue * 0.08)); // min 8% of CA
  const margePerdueParMois = Math.max(opportunityNonExploitee, 500);
  const margePerdueParAn = margePerdueParMois * 12;
  
  // Simulated savings
  const simulatedPrimeCost = simulatedFoodCost + simulatedMasseSalariale;
  const simulatedExcess = Math.max(0, simulatedPrimeCost - optimalPrimeCost);
  const simulatedMargePerdueParMois = Math.round((simulatedExcess / 100) * caValue);
  const economiesParMois = Math.max(margePerdueParMois - simulatedMargePerdueParMois, Math.round(caValue * 0.05));
  const economiesParAn = economiesParMois * 12;
  
  // 12-month projection — vision selling
  const projectionStatuQuo = caValue * 12; // same CA × 12
  const projectionOptimise = Math.round(caValue * 1.15 * 12); // +15% optimized scenario
  const projectionDelta = projectionOptimise - projectionStatuQuo;
  
  // Potential growth
  const potentielMarketingPercent = 20;
  const potentielMarketingMois = Math.round(caValue * (potentielMarketingPercent / 100));
  
  // Score calculation
  const scoreMax = 100;
  let score = 50;
  if (primeCost <= 60) score += 20;
  else if (primeCost <= 70) score += 10;
  else score -= 10;
  if (answers.instagram_freq >= 3) score += 10;
  if (answers.note_google >= 4.5) score += 10;
  if (answers.plat_signature === 'oui') score += 10;
  score = Math.max(0, Math.min(scoreMax, score));

  // Diagnostic level
  const getDiagnosticLevel = () => {
    if (primeCost > 75) return { level: 'critical', label: 'Critique', color: theme.red, bg: 'bg-red-50', border: 'border-red-300' };
    if (primeCost > 65) return { level: 'danger', label: 'En danger', color: theme.orange, bg: 'bg-orange-50', border: 'border-orange-300' };
    if (primeCost > 60) return { level: 'warning', label: 'À optimiser', color: theme.yellow, bg: 'bg-yellow-50', border: 'border-yellow-300' };
    return { level: 'good', label: 'Bon', color: theme.green, bg: 'bg-green-50', border: 'border-green-300' };
  };
  
  const diagnostic = getDiagnosticLevel();

  // Ouverture mode flag — comes from backend doc.mode === 'ouverture'
  const isOuverture = meta.mode === 'ouverture';
  const riskScore = meta.risk_score ?? 50;
  const readinessScore = meta.readiness_score ?? (100 - riskScore);
  const ouvertureDiag = meta.diagnosis || { level: 'VIGILANCE', title: 'Plusieurs angles morts à neutraliser', color: '#F59E0B' };
  // Map chantier values → labels
  const CHANTIER_LABELS = {
    marque: { ic: '🎨', label: 'Construire la marque' },
    concept: { ic: '💡', label: 'Définir le concept' },
    ops: { ic: '⚙️', label: 'Structurer l\'opérationnel' },
    equipe: { ic: '👨‍🍳', label: 'Recruter & former l\'équipe' },
    marketing: { ic: '📱', label: 'Stratégie marketing & lancement' },
    local: { ic: '🏢', label: 'Trouver / négocier le local' },
    business_plan: { ic: '📊', label: 'Business plan & financement' },
    travaux: { ic: '🔨', label: 'Aménagement & travaux' },
  };
  const chantiers = Array.isArray(answers.ouverture_chantiers) ? answers.ouverture_chantiers : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-t-transparent rounded-full"
          style={{ borderColor: theme.orange, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative"
      style={{ background: '#f5f3f0' }}
      data-testid="results-page"
    >
      {/* Halo background retiré — la page est désormais light, les cartes Dark Dossier ressortent par contraste */}

      {/* Philosophy gate — shown once per audit, plants 80/20 rule before user sees diagnostic */}
      <AnimatePresence>
        {!philosophyDismissed && (
          <PhilosophyGate
            key="philosophy-gate"
            onDismiss={handlePhilosophyDismiss}
            restoName={answers.resto_name}
          />
        )}
      </AnimatePresence>

      {/* Header — sober dark with orange accent line + Pixar avatar */}
      <div
        className="relative px-6 py-8 text-center border-b border-white/10"
        style={{ background: 'rgba(0,0,0,0.25)' }}
      >
        {/* Pixar Avatar centered above title */}
        <div className="flex justify-center mb-3">
          <SabrilPixarAvatar size={72} />
        </div>

        {/* Accent line */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-[#F05A1A] to-transparent" />
        <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] mb-1">
          🩺 Dossier confidentiel · audité par Sabril
        </p>
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-white font-bebas text-3xl sm:text-4xl tracking-wide leading-none"
        >
          DIAGNOSTIC RESTAURANT
        </motion.h1>
        {answers.resto_name && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-white/70 text-base sm:text-lg mt-2 italic"
          >
            « {answers.resto_name} »
          </motion.p>
        )}
      </div>

      {/* Content */}
      <div className="relative max-w-lg mx-auto px-4 sm:px-5 py-8 space-y-6 [&_.bg-white]:ring-1 [&_.bg-white]:ring-white/10 [&_.bg-white]:shadow-[0_8px_30px_rgba(0,0,0,0.25)]">

        {/* ========= OUVERTURE BANNER — Replaces financial diagnostic when mode=ouverture ========= */}
        {isOuverture && (
          <PremiumDarkCard
            kicker={`🚀 Diagnostic d'ouverture${answers.resto_name ? ' · ' + answers.resto_name : ''}`}
            kickerColor={readinessScore >= 70 ? 'green' : readinessScore >= 50 ? 'amber' : 'orange'}
            title={
              <>
                Ton projet est<br />
                <span style={{ color: ouvertureDiag.color }}>
                  {ouvertureDiag.level === 'EXCELLENT' && 'EXCEPTIONNELLEMENT PRÉPARÉ'}
                  {ouvertureDiag.level === 'SOLIDE' && 'BIEN CADRÉ'}
                  {ouvertureDiag.level === 'VIGILANCE' && 'À CONSOLIDER'}
                  {ouvertureDiag.level === 'CRITIQUE' && 'À RECADRER D\'URGENCE'}
                </span>
              </>
            }
            subtitle={
              <>
                {ouvertureDiag.title}. Voici les angles morts identifiés sur ton projet, et ce qu'on installe ensemble pour les neutraliser.
              </>
            }
            testid="ouverture-diagnostic-banner"
          >
            {/* Risk & Readiness scores side by side */}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 flex flex-col items-center justify-center">
                <ProgressRing
                  percentage={readinessScore}
                  size={84}
                  color="#22C55E"
                />
                <p className="text-white/50 text-[10px] uppercase tracking-widest font-bold mt-2">Préparation</p>
                <p className="text-green-400 text-xs font-black">{readinessScore}/100</p>
              </div>
              <div className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 flex flex-col items-center justify-center">
                <ProgressRing
                  percentage={riskScore}
                  size={84}
                  color={riskScore >= 70 ? '#EF4444' : riskScore >= 50 ? '#F59E0B' : '#22C55E'}
                />
                <p className="text-white/50 text-[10px] uppercase tracking-widest font-bold mt-2">Risque</p>
                <p className="text-xs font-black" style={{ color: riskScore >= 70 ? '#EF4444' : riskScore >= 50 ? '#F59E0B' : '#22C55E' }}>{riskScore}/100</p>
              </div>
            </div>

            {/* Chantiers cochés — Ce qu'on construit ensemble */}
            {chantiers.length > 0 && (
              <div className="bg-white/[0.04] rounded-2xl p-4 border border-white/10 mb-4" data-testid="ouverture-chantiers-list">
                <p className="text-[10px] font-black text-[#F05A1A] uppercase tracking-widest mb-3">
                  🛠️ Ce qu'on construit ensemble · {chantiers.length} chantier{chantiers.length > 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {chantiers.map((c) => {
                    const meta = CHANTIER_LABELS[c];
                    if (!meta) return null;
                    return (
                      <div key={c} className="flex items-center gap-3 py-1.5">
                        <span className="text-lg" aria-hidden="true">{meta.ic}</span>
                        <span className="text-white/85 text-sm font-medium">{meta.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Key project facts grid */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              {answers.ouverture_echeance && (
                <div className="bg-white/[0.04] rounded-xl p-3 border border-white/10">
                  <p className="text-white/45 text-[10px] uppercase tracking-wider mb-0.5">Échéance</p>
                  <p className="text-white font-bold">
                    {answers.ouverture_echeance === 'moins_3m' && '< 3 mois'}
                    {answers.ouverture_echeance === '3_6m' && '3 à 6 mois'}
                    {answers.ouverture_echeance === '6_12m' && '6 à 12 mois'}
                    {answers.ouverture_echeance === 'plus_12m' && '+ 12 mois'}
                  </p>
                </div>
              )}
              {answers.ouverture_local && (
                <div className="bg-white/[0.04] rounded-xl p-3 border border-white/10">
                  <p className="text-white/45 text-[10px] uppercase tracking-wider mb-0.5">Local</p>
                  <p className="text-white font-bold">
                    {answers.ouverture_local === 'signe' && '✅ Signé'}
                    {answers.ouverture_local === 'en_nego' && '🤝 En négo'}
                    {answers.ouverture_local === 'recherche' && '🔍 Recherche'}
                    {answers.ouverture_local === 'pas_encore' && '🤔 Pas commencé'}
                  </p>
                </div>
              )}
              {answers.ouverture_budget && (
                <div className="bg-white/[0.04] rounded-xl p-3 border border-white/10">
                  <p className="text-white/45 text-[10px] uppercase tracking-wider mb-0.5">Budget</p>
                  <p className="text-white font-bold">{Number(answers.ouverture_budget).toLocaleString('fr-FR')}€</p>
                </div>
              )}
              {answers.ouverture_experience && (
                <div className="bg-white/[0.04] rounded-xl p-3 border border-white/10">
                  <p className="text-white/45 text-[10px] uppercase tracking-wider mb-0.5">Expérience</p>
                  <p className="text-white font-bold">
                    {answers.ouverture_experience === 'jamais' && '🌱 Première fois'}
                    {answers.ouverture_experience === 'une_fois' && '🎯 1 ouverture'}
                    {answers.ouverture_experience === 'plusieurs' && '🏆 Plusieurs'}
                  </p>
                </div>
              )}
            </div>
          </PremiumDarkCard>
        )}

        {!isOuverture && (<>

        {/* ========= ÉTAPE 1 : MOMENT DE VÉRITÉ — Intrigue / choc / prise de conscience ========= */}
        <AuditRealizationSection
          restoName={answers.resto_name}
          margePerdueParAn={margePerdueParAn}
        />

        {/* Séparateur narratif */}
        <div className="flex items-center gap-3 my-2 text-white/40">
          <span className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] font-black uppercase tracking-widest">↓ Étape 2 · Ton diagnostic</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        
        {/* SHOCK BLOCK — Premium Dark Card cohérent avec le moment de vérité */}
        <PremiumDarkCard
          kicker={`🩺 Diagnostic ${answers.resto_name ? 'de ' + answers.resto_name : 'de votre resto'}`}
          kickerColor="orange"
          title={
            <>
              {heroVariant === 'b' ? (
                <>Vous pouvez gagner<br/><span className="text-green-400">+{margePerdueParMois.toLocaleString('fr-FR')}€</span> par mois</>
              ) : (
                <>Vous laissez<br/><span className="text-red-400">{margePerdueParMois.toLocaleString('fr-FR')}€</span> sur la table chaque mois</>
              )}
            </>
          }
          subtitle={
            <>
              {heroVariant === 'b'
                ? <>Soit <span className="text-white font-bold">+{margePerdueParAn.toLocaleString('fr-FR')}€/an</span> de marge à débloquer dès ce mois-ci.</>
                : <>Soit <span className="text-white font-bold">{margePerdueParAn.toLocaleString('fr-FR')}€/an</span> que vous laissez partir — sans vous en rendre compte.</>}
            </>
          }
          testid={`shock-block-variant-${heroVariant}`}
        >
          {/* Score + verdict */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {/* Score circulaire */}
            <div className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 flex flex-col items-center justify-center">
              <ProgressRing
                percentage={score}
                size={84}
                strokeWidth={8}
                color={score >= 60 ? theme.green : score >= 40 ? theme.yellow : theme.red}
              />
              <p className="text-white/55 text-[10px] font-black uppercase tracking-widest mt-2">Score / 100</p>
            </div>

            {/* Verdict */}
            <div className="sm:col-span-2 rounded-2xl p-4 bg-white/[0.04] border border-white/10 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">
                  {primeCost > 70 ? '🔴' : primeCost > 60 ? '🟠' : '🟢'}
                </span>
                <p className={`text-sm font-bold ${
                  primeCost > 70 ? 'text-red-400' :
                  primeCost > 60 ? 'text-amber-400' :
                  'text-green-400'
                }`}>
                  {primeCost > 70 ? 'Critique — action immédiate' :
                   primeCost > 60 ? 'Intermédiaire — zones à corriger' :
                   'Bon · mais sous-exploité'}
                </p>
              </div>
              <p className="text-white/65 text-[12px] sm:text-xs leading-relaxed italic">
                {primeCost <= 60
                  ? `Vous faites partie des 30% qui gèrent bien leurs chiffres. Mais vous pourriez être dans les 10% qui les explosent.`
                  : primeCost <= 70
                  ? `Vous avez les bases. Mais 60% des restos à votre niveau stagnent — parce qu'ils ne savent pas quoi faire ensuite.`
                  : `Chaque jour sans correction, l'écart se creuse. Les 20% du marché les plus rentables font ce que vous ne faites pas encore.`}
              </p>
            </div>
          </div>

          {/* Mini A/B variant tag — hidden visually, kept for analytics */}
          <p data-testid={`hero-urgency-variant-${heroVariant}`} className="sr-only">variant-{heroVariant}</p>
        </PremiumDarkCard>

        {/* AVATAR — Accueil Sabril après le shock block */}
        <div className="my-2" data-testid="results-shock-avatar-wrapper">
          <AnimatedAvatar
            size="md"
            position="right"
            bubbleArrow="left"
            delay={0.4}
            testid="results-shock-avatar"
            message={[
              `Salut ${answers.resto_name ? answers.resto_name : 'à vous'} 👋 Voici votre diagnostic — lisez-le calmement.`,
              `Score ${score}/100. Pas de panique — j'ai un plan pour vous.`,
              `On a fait grimper +40 restos. Le tien peut suivre.`,
            ]}
          />
        </div>

        {/* CARTE PREMIUM — LÀ OÙ ÇA FUIT (résumé chiffres clés en bento) */}
        <PremiumDarkCard
          kicker="💸 Là où ça fuit chez vous"
          kickerColor="red"
          title={<>Les 4 chiffres<br/><span className="text-red-400">qui décident</span> de votre marge</>}
          subtitle={<>Voici les 4 indicateurs qui font la différence entre un resto qui survit et un resto qui scale — et où vous en êtes <span className="text-white font-bold">aujourd'hui</span>.</>}          testid="metrics-summary-card"
        >
          <div className="grid grid-cols-2 gap-3 mb-2">
            <MetricTile
              label="Food Cost"
              value={foodCost}
              unit="%"
              status={foodCost > 32 ? 'danger' : foodCost > 28 ? 'warning' : 'ok'}
              detail={foodCost > 32 ? 'Trop haut · cible 26-30%' : foodCost > 28 ? 'Limite · à serrer' : 'Bon niveau · à maintenir'}
              testid="metric-food-cost"
            />
            <MetricTile
              label="Masse Salariale"
              value={masseSalariale}
              unit="%"
              status={masseSalariale > 35 ? 'danger' : masseSalariale > 30 ? 'warning' : 'ok'}
              detail={masseSalariale > 35 ? 'Élevée · sur-staff probable' : masseSalariale > 30 ? 'À surveiller' : 'Optimisé'}
              testid="metric-ms"
            />
            <MetricTile
              label="Prime Cost"
              value={primeCost}
              unit="%"
              status={primeCost > 70 ? 'danger' : primeCost > 60 ? 'warning' : 'ok'}
              detail={primeCost > 70 ? 'Critique · seuil rouge' : primeCost > 60 ? 'Zone à risque' : 'Sous le seuil pro · top'}
              testid="metric-prime"
            />
            <MetricTile
              label="Marge perdue/an"
              value={Math.round(margePerdueParAn / 1000)}
              unit="k€"
              status={margePerdueParAn > 30000 ? 'danger' : margePerdueParAn > 12000 ? 'warning' : 'ok'}
              detail={margePerdueParAn > 30000 ? "C'est un salaire annuel entier" : margePerdueParAn > 12000 ? "Plusieurs mois de loyer" : 'Limité — bon pilotage'}
              testid="metric-margin-lost"
            />
          </div>
          <p className="text-white/45 text-[11px] italic leading-snug">
            Pourcentages calculés à partir de tes réponses · benchmarks restos premium tournant à 30k€+/mois
          </p>
        </PremiumDarkCard>

        {/* QUICK WIN - Premium Dark avec accent vert */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="relative rounded-2xl p-5 overflow-hidden border border-green-500/30"
          style={{ background: 'linear-gradient(180deg, #0e1a14 0%, #14241c 100%)' }}
        >
          {/* Halo green ambient */}
          <div
            className="absolute inset-0 opacity-50 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 20% 30%, rgba(16,185,129,0.22) 0%, transparent 55%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.12) 0%, transparent 55%)',
            }}
            aria-hidden="true"
          />

          <div className="relative flex items-start gap-3">
            <span className="text-3xl" style={{ filter: 'drop-shadow(0 0 10px rgba(16,185,129,0.6))' }}>💰</span>
            <div className="flex-1 min-w-0">
              <p className="font-black text-green-400 text-[10px] uppercase tracking-widest mb-2">
                ⚡ Ton quick win — applicable dès demain
              </p>
              {(() => {
                const ticketMoyen = answers.ticket_moyen || 18;
                const couvertsJour = Math.round((caValue / 30) / ticketMoyen);
                const couvertsMois = couvertsJour * 25;
                const gainTicket = Math.round(couvertsMois * 2.5);
                const gainFoodCost = Math.round(caValue * 0.015);
                const gainMasse = Math.round(caValue * 0.02);

                if (foodCost > 32) {
                  return (
                    <>
                      <p className="text-white text-base font-bold leading-snug">
                        +<span className="text-green-400" style={{ textShadow: '0 0 18px rgba(16,185,129,0.5)' }}>{gainFoodCost.toLocaleString('fr-FR')}€</span>/mois en retravaillant 3 plats.
                      </p>
                      <p className="text-white/65 text-xs mt-1.5 leading-relaxed">
                        Identifiez vos 3 plats avec le food cost le plus élevé. +1€ de prix OU -10% de portion. Les clients ne verront rien. Vous récupérez des marges immédiatement.
                      </p>
                    </>
                  );
                } else if (masseSalariale > 33) {
                  return (
                    <>
                      <p className="text-white text-base font-bold leading-snug">
                        +<span className="text-green-400" style={{ textShadow: '0 0 18px rgba(16,185,129,0.5)' }}>{gainMasse.toLocaleString('fr-FR')}€</span>/mois en ajustant ton planning.
                      </p>
                      <p className="text-white/65 text-xs mt-1.5 leading-relaxed">
                        Analyse tes 2h les moins rentables de la semaine → réduis d'1h le planning sur ces créneaux. Personne ne le remarque sauf ta comptabilité.
                      </p>
                    </>
                  );
                } else {
                  return (
                    <>
                      <p className="text-white text-base font-bold leading-snug">
                        +<span className="text-green-400" style={{ textShadow: '0 0 18px rgba(16,185,129,0.5)' }}>{gainTicket.toLocaleString('fr-FR')}€</span>/mois avec un tweak menu.
                      </p>
                      <p className="text-white/65 text-xs mt-1.5 leading-relaxed">
                        Ajoute <span className="font-bold text-white">"Avec supplément ou formule à +3€ ?"</span> comme script obligatoire en commande. +2,50€ de ticket moyen × {couvertsMois.toLocaleString('fr-FR')} couverts = {gainTicket.toLocaleString('fr-FR')}€/mois. Sans un client de plus.
                      </p>
                    </>
                  );
                }
              })()}
            </div>
          </div>
        </motion.div>
        
        {/* Comparison with Market — Premium Dark */}
        <PremiumDarkCard
          kicker="📊 Position vs marché"
          kickerColor="orange"
          title={<>TU ES <span className="text-[#F05A1A]">{primeCost > 65 ? 'EN ZONE ROUGE' : primeCost > 60 ? 'DANS LA MOYENNE' : 'AU-DESSUS DE 60%'}</span></>}
          subtitle={`Comparé aux restos similaires en France.`}
          testid="results-market-comparison"
        >
          {/* Visual comparison bar */}
          <div className="relative h-8 bg-white/[0.06] rounded-full overflow-hidden mb-2">
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 to-green-500"
              style={{ width: '22%' }}
            />
            <div
              className="absolute top-0 h-full bg-gradient-to-r from-amber-400 to-amber-500"
              style={{ left: '22%', width: '36%' }}
            />
            <div
              className="absolute top-0 h-full bg-gradient-to-r from-red-400 to-red-500"
              style={{ left: '58%', width: '42%' }}
            />
            {/* Your position */}
            <motion.div
              initial={{ left: '0%' }}
              animate={{ left: `${Math.min(95, Math.max(5, 100 - score))}%` }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-4 border-[#1a1a1a] rounded-full shadow-lg"
              style={{ boxShadow: '0 0 14px rgba(255,255,255,0.6), 0 2px 6px rgba(0,0,0,0.4)' }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-white/45 font-bold uppercase tracking-wider">
            <span>Top performers</span>
            <span>Moyenne</span>
            <span>En difficulté</span>
          </div>
        </PremiumDarkCard>
        
        {/* Score Card / Benchmark Table / Marge Perdue séparée / EBE Card / Potentiel Développement / Points prioritaires —
            sections retirées pour resserrer le flow : les infos sont déjà fusionnées dans le bento "LÀ OÙ ÇA FUIT" + Prime Cost interactive */}

        {/* Prime Cost - Interactive */}
        <InteractiveCard highlighted={primeCost > (getBenchmark(answers.resto_type).prime + 5)}>
          <h2 className="text-orange-400 text-[10px] font-black uppercase tracking-[3px] mb-1">
            💰 Rentabilité
          </h2>
          <p className="text-[11px] text-white/55 mb-4">
            Comparé aux <strong className="text-white/85">restos {getBenchmark(answers.resto_type).label.toLowerCase()}</strong> du marché français.
          </p>

          <div className="space-y-4">
            <ComparisonRow
              label="Food cost"
              current={foodCost}
              optimal={getBenchmark(answers.resto_type).food}
              niche={getBenchmark(answers.resto_type).label.toLowerCase()}
              kind="food"
              delay={0.1}
              dark
            />
            <ComparisonRow
              label="Masse salariale"
              current={masseSalariale}
              optimal={getBenchmark(answers.resto_type).ms}
              niche={getBenchmark(answers.resto_type).label.toLowerCase()}
              kind="ms"
              delay={0.2}
              dark
            />
          </div>

          <div className="mt-6 pt-4 border-t border-white/10">
            <div className="flex justify-between items-center">
              <div>
                <span className="font-bold text-white block">Prime Cost Total</span>
                <span className="text-[11px] text-white/55">Benchmark {getBenchmark(answers.resto_type).label.toLowerCase()} : <strong className="text-white/80">{getBenchmark(answers.resto_type).prime}%</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-4xl font-black" style={{ color: diagnostic.color, textShadow: `0 0 22px ${diagnostic.color}66` }}>
                  {primeCost}%
                </span>
                <span className="text-sm text-white/45">({(primeCost / 100 * caValue).toLocaleString('fr-FR')}€/mois)</span>
              </div>
            </div>
          </div>

          {/* Diagnostic Alert — Dark */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-4 p-4 rounded-xl border backdrop-blur-sm"
            style={{
              background: `${diagnostic.color}15`,
              borderColor: `${diagnostic.color}40`,
            }}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: diagnostic.color }} />
              <div>
                <p className="font-bold" style={{ color: diagnostic.color }}>
                  Diagnostic : Rentabilité {diagnostic.label.toLowerCase()}
                </p>
                <p className="text-sm text-white/70 mt-1">
                  {primeCost > (getBenchmark(answers.resto_type).prime + 5)
                    ? `Vous êtes ${primeCost - getBenchmark(answers.resto_type).prime} points au-dessus du benchmark ${getBenchmark(answers.resto_type).label.toLowerCase()} (${getBenchmark(answers.resto_type).prime}%). Optimisation urgente recommandée.`
                    : `Vous êtes proche du benchmark ${getBenchmark(answers.resto_type).label.toLowerCase()} (${getBenchmark(answers.resto_type).prime}%). Quelques optimisations permettraient d'améliorer encore votre marge.`}
                </p>
              </div>
            </div>
          </motion.div>
        </InteractiveCard>

        {/* EBE / Benchmark table / Marge perdue séparée — fusionnés dans le bento "LÀ OÙ ÇA FUIT" et Prime Cost interactive */}

        {/* SIMULATEUR INTERACTIF — Premium Dark */}
        <PremiumDarkCard
          kicker="✨ Simulateur d'économies"
          kickerColor="orange"
          title={<>SI TU CORRIGES <span className="text-[#F05A1A]">TES CURSEURS</span>…</>}
          subtitle="Bougez les sliders, je vous montre combien vous récupérez par mois."
          testid="results-simulator-card"
        >
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={() => setShowSimulator(!showSimulator)}
              className="text-[#F05A1A] text-sm font-medium flex items-center gap-1 hover:underline"
            >
              {showSimulator ? 'Masquer' : 'Afficher'}
              <motion.span animate={{ rotate: showSimulator ? 180 : 0 }}>
                <ChevronDown className="w-4 h-4" />
              </motion.span>
            </button>
          </div>

          <AnimatePresence>
            {showSimulator && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-6"
              >
                <SimulationSlider
                  label="Food cost cible"
                  value={simulatedFoodCost}
                  onChange={setSimulatedFoodCost}
                  min={20}
                  max={50}
                  color={simulatedFoodCost <= 30 ? theme.green : theme.orange}
                  description="Objectif recommandé : 25-30%"
                  dark
                />

                <SimulationSlider
                  label="Masse salariale cible"
                  value={simulatedMasseSalariale}
                  onChange={setSimulatedMasseSalariale}
                  min={20}
                  max={50}
                  color={simulatedMasseSalariale <= 30 ? theme.green : theme.orange}
                  description="Objectif recommandé : 25-30%"
                  dark
                />

                <div className="bg-white/[0.04] backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-white/70">Prime Cost simulé</span>
                    <span className="font-bold text-xl" style={{ color: simulatedPrimeCost <= 60 ? '#10b981' : '#F05A1A' }}>
                      {simulatedPrimeCost}%
                    </span>
                  </div>

                  {economiesParMois > 0 && (
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="rounded-lg p-4 mt-4 border border-green-500/30 bg-green-500/10 backdrop-blur-sm"
                    >
                      <p className="text-green-400 text-sm font-medium text-center mb-2">
                        🎉 Vous pourriez économiser :
                      </p>
                      <div className="text-center">
                        <span className="text-green-400 font-bebas text-5xl" style={{ textShadow: '0 0 22px rgba(16,185,129,0.5)' }}>
                          +{economiesParMois.toLocaleString('fr-FR')}€
                        </span>
                        <span className="text-green-400 text-lg">/mois</span>
                      </div>
                      <p className="text-green-400/80 text-center mt-2 text-sm">
                        Soit <strong>{economiesParAn.toLocaleString('fr-FR')}€/an</strong>
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!showSimulator && economiesParMois > 0 && (
            <div className="rounded-lg p-3 border border-green-500/30 bg-green-500/10 text-center">
              <span className="text-green-400 font-bold">
                Économie potentielle : +{economiesParMois.toLocaleString('fr-FR')}€/mois
              </span>
            </div>
          )}
        </PremiumDarkCard>

        {/* Potentiel Développement & Points prioritaires dynamiques — retirés : déjà couverts par Quick Win + bento "LÀ OÙ ÇA FUIT" + Simulateur. La projection 12 mois ci-dessous fait la synthèse. */}

        {/* 12-Month Projection — Premium Dark */}
        <PremiumDarkCard
          kicker="📈 Projection sur 12 mois"
          kickerColor="amber"
          title={<>SI TU APPLIQUES <span className="text-amber-400">CES OPTIMISATIONS</span>…</>}
          testid="results-projection-card"
        >
          <div className="text-center mb-5">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <span
                className="font-bebas text-6xl sm:text-7xl tracking-tight"
                style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #F05A1A 50%, #fbbf24 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '0 0 40px rgba(240,90,26,0.35)',
                }}
              >
                +{(margePerdueParAn + Math.round(caValue * 0.15 * 12)).toLocaleString('fr-FR')}€
              </span>
            </motion.div>
            <p className="text-white/55 text-sm mt-1">de gain potentiel sur 12 mois</p>
          </div>

          <div className="bg-white/[0.04] backdrop-blur-sm rounded-xl p-4 border border-white/10 space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-white/10">
              <span className="text-white/70">Optimisation prime cost</span>
              <span className="font-bold text-green-400">+{margePerdueParAn.toLocaleString('fr-FR')}€</span>
            </div>
            <div className="flex justify-between py-2 border-b border-white/10">
              <span className="text-white/70">Développement CA (+15%)</span>
              <span className="font-bold text-green-400">+{Math.round(caValue * 0.15 * 12).toLocaleString('fr-FR')}€</span>
            </div>
            <div className="flex justify-between py-2 font-bold">
              <span className="text-amber-400">Total potentiel</span>
              <span className="text-amber-400">+{(margePerdueParAn + Math.round(caValue * 0.15 * 12)).toLocaleString('fr-FR')}€</span>
            </div>
          </div>
        </PremiumDarkCard>

        </>)}

        {/* ========= STORYTELLING — 4 piliers (ADN / Pilotage / Réseaux / 360°) ========= */}
        <ResultsStorytellingBlock restoName={answers.resto_name} firstName={answers.first_name} />

        {/* ========= RÉCAP DE VOTRE AUDIT — 4 chiffres clés avant le choix ========= */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto px-4 sm:px-0 mt-10 mb-10"
          data-testid="audit-recap-card"
        >
          <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 20px 50px -20px rgba(240,90,26,0.20)' }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#F05A1A] to-[#DC2626] px-5 py-3 flex items-center justify-between">
              <p className="text-white text-[10px] font-black uppercase tracking-widest">
                📋 Votre audit — synthèse
              </p>
              <p className="text-white/85 text-[10px] font-bold uppercase tracking-wider">
                {answers.first_name ? `${answers.first_name} · ` : ''}{answers.resto_name || 'Votre resto'}
              </p>
            </div>

            {/* 4 metrics grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100">
              {/* Score */}
              <div className="p-5 text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 mb-2">Score global</p>
                <p className="text-3xl font-black tabular-nums" style={{ color: score >= 70 ? theme.green : score >= 50 ? theme.orange : theme.red }}>
                  {score}<span className="text-lg text-gray-400 font-normal">/100</span>
                </p>
                <p className="text-[10px] font-bold text-gray-500 mt-1">{diagnostic.label}</p>
              </div>

              {/* Prime Cost */}
              <div className="p-5 text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 mb-2">Prime Cost</p>
                <p className="text-3xl font-black tabular-nums" style={{ color: primeCost > 65 ? theme.red : primeCost > 60 ? theme.orange : theme.green }}>
                  {primeCost.toFixed(0)}<span className="text-lg text-gray-400 font-normal">%</span>
                </p>
                <p className="text-[10px] font-bold text-gray-500 mt-1">
                  {primeCost > 65 ? 'À corriger' : primeCost > 60 ? 'À surveiller' : 'Sain'}
                </p>
              </div>

              {/* Marge perdue */}
              <div className="p-5 text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 mb-2">Marge perdue</p>
                <p className="text-3xl font-black text-red-600 tabular-nums">
                  −{Math.round(margePerdueParMois).toLocaleString('fr-FR')}<span className="text-lg text-gray-400 font-normal">€</span>
                </p>
                <p className="text-[10px] font-bold text-gray-500 mt-1">par mois</p>
              </div>

              {/* Priorité #1 */}
              <div className="p-5 text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 mb-2">Priorité #1</p>
                <p className="text-base font-black text-gray-900 leading-tight">
                  {primeCost > 70
                    ? 'Marges'
                    : foodCost > 32
                      ? 'Food cost'
                      : (answers.instagram_freq !== undefined && answers.instagram_freq < 3)
                        ? 'Visibilité'
                        : (answers.plat_signature === 'non')
                          ? 'Menu'
                          : 'Structuration'}
                </p>
                <p className="text-[10px] font-bold text-orange-600 mt-1">À traiter en 1er</p>
              </div>
            </div>

            {/* ═══ Chantier associé — lien direct vers le wizard ═══ */}
            {(() => {
              const chantierMap = {
                'Marges':       { n: '05', title: 'Fiches techniques · Food cost & marges', price: 1990 },
                'Food cost':    { n: '05', title: 'Fiches techniques · Food cost & marges', price: 1990 },
                'Visibilité':   { n: '10', title: 'Univers visuel & Calendrier éditorial', price: 2990 },
                'Menu':         { n: '04', title: 'Menu Gagnant',                           price: 1990 },
                'Structuration':{ n: '00', title: 'Coaching dirigeant · 12h',               price: 2990 },
              };
              const priority = primeCost > 70
                ? 'Marges'
                : foodCost > 32
                  ? 'Food cost'
                  : (answers.instagram_freq !== undefined && answers.instagram_freq < 3)
                    ? 'Visibilité'
                    : (answers.plat_signature === 'non')
                      ? 'Menu'
                      : 'Structuration';
              const c = chantierMap[priority];
              return (
                <button
                  type="button"
                  onClick={() => navigate(`/configurer?chantier=${c.n}`)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 hover:bg-orange-50/40 transition-colors group"
                  data-testid="audit-recap-chantier-link"
                >
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-[9px] font-black uppercase tracking-widest text-orange-600 mb-1">
                      Le chantier associé à votre priorité
                    </p>
                    <p className="text-sm font-black text-gray-900 leading-tight truncate">
                      {c.n} · {c.title}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Dès {c.price.toLocaleString('fr-FR')} € HT · Configurable avec Sabril
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-orange-600 whitespace-nowrap group-hover:gap-2 transition-all">
                    Ajouter
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                </button>
              );
            })()}

            {/* Bottom band — bridge to choice */}
            <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 text-center">
              <p className="text-gray-700 text-xs leading-snug">
                Voici la synthèse en 4 chiffres. <span className="font-bold">Ce qui vient ensuite, c'est votre décision</span> — comment on avance ensemble.
              </p>
            </div>
          </div>
        </motion.div>

        {/* ========= NARRATIVE BREAK — Rupture diagnostic → offres (avec 2 cartes Option 1 / Option 2) ========= */}
        <NarrativeChoiceGate mode={isOuverture ? 'ouverture' : 'standard'} firstName={answers.first_name} />

        {/* ========= QUICK ACCESS — 3 offres en pills iOS ========= */}
        <OffersQuickAccess
          variant="light"
          kicker="🎯 Vos options pour passer à l'action"
          title="Choisissez votre format."
          hideCurrentPage={false}
        />

        {/* AVATAR — Push humain doux, sans pitch d'offre */}
        <div className="my-4" data-testid="results-cta-avatar-wrapper">
          <AnimatedAvatar
            size="lg"
            position="right"
            bubbleArrow="left"
            pulse
            delay={0.2}
            testid="results-cta-avatar"
            message={[
              `${answers.resto_name ? answers.resto_name + ', s' : 'S'}i vous voulez qu'on en discute, je suis là 🤝`,
              `15 min de chat avec Sabril · pas de pitch, juste une vraie conversation.`,
              `On regarde ensemble ce que vous pouvez faire — avec ou sans moi.`,
            ]}
          />
        </div>

        {/* CTA Section — Conversation simple avec Sabril, version Premium Dark Card */}
        <PremiumDarkCard
          kicker="💬 Une conversation, c'est tout"
          kickerColor="green"
          title={
            <>
              Si vous voulez qu'on en parle,<br/>
              <span className="text-[#F05A1A]">j'ai 15 min pour vous.</span>
            </>
          }
          subtitle={
            <>
              Pas de pitch d'offre, pas d'argumentaire commercial.
              On regarde ensemble ce que vous pouvez faire — <span className="text-white font-bold">avec ou sans moi</span>.
              Vous décidez ensuite.
            </>
          }
          testid="results-conversation-cta"
        >
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => {
                try { trackHero('cta_prsnl_click'); } catch (_) { /* noop */ }
                try { window.dispatchEvent(new CustomEvent('sabril:open', { detail: { intent: 'project' } })); } catch (e) { /* noop */ }
              }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F05A1A] to-[#FF7A3D] hover:from-[#FF6A2A] hover:to-[#FF8C54] text-white font-black uppercase tracking-wider text-sm sm:text-base px-6 py-4 rounded-2xl shadow-[0_12px_30px_rgba(240,90,26,0.40)] transition-all"
              data-testid="results-talk-sabril-btn"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Parler à Sabril</span>
            </button>
            <p className="text-white/55 text-[11px] italic mt-3">
              Réponse immédiate · Aucun engagement
            </p>
          </div>
        </PremiumDarkCard>

        {/* Closing Message — text adapté pour bg light */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center py-8 px-6"
        >
          <p className="text-[#1a1a1a]/75 text-base italic mb-3 leading-relaxed font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
            « Vous n'avez pas un problème de restaurant.<br/>
            <span className="text-[#1a1a1a] font-medium">Vous avez un problème de structure.</span><br/>
            Et ça vous coûte de l'argent <span className="text-[#F05A1A] font-bold">tous les mois</span>. »
          </p>
          <p className="text-[#F05A1A] text-[11px] font-black uppercase tracking-[3px]">— Sabril · Fondateur PRSNL</p>
        </motion.div>

        {/* SOCIAL PROOF — Stats + Témoignages, déplacés tout en bas */}
        <PremiumDarkCard
          kicker="⭐ Ils l'ont fait avant vous"
          kickerColor="amber"
          title={<>Plus de <span className="text-amber-400">127 restaurateurs</span><br/>nous font déjà confiance</>}
          subtitle="Les chiffres + ce qu'ils en pensent."
          testid="social-proof-section"
        >
          {/* Stats trio */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { n: '127+', label: 'Restaurateurs accompagnés' },
              { n: '2,3M€', label: 'Générés en 2024' },
              { n: '4.9★', label: 'Note moyenne' },
            ].map((s, i) => (
              <div key={i} className="rounded-xl p-3 bg-white/[0.04] border border-white/10 text-center">
                <p className="text-lg sm:text-xl font-black text-amber-400">{s.n}</p>
                <p className="text-[10px] text-white/55 leading-tight mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* 3 témoignages compacts */}
          <div className="space-y-2">
            {[
              { name: 'Karim', role: 'Pizzeria · Lyon', q: "J'étais sceptique. 30 jours après, +4 200€ de CA. Zéro théorie, que de l'action concrète.", avatar: 'K', color: 'bg-orange-500' },
              { name: 'Élodie', role: 'Bistrot · Toulouse', q: "Prime cost descendu de 71% à 63% en 3 semaines. J'ai récupéré 2 800€ de marge sans perdre un client.", avatar: 'E', color: 'bg-purple-500' },
              { name: 'Yann', role: 'Brasserie · Lille', q: "L'audit m'a ouvert les yeux. J'ai enfin compris où partait mon argent.", avatar: 'Y', color: 'bg-emerald-500' },
            ].map((t, i) => (
              <div key={i} className="rounded-xl p-3 bg-white/[0.04] border border-white/10">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full ${t.color} text-white font-black flex items-center justify-center flex-shrink-0 text-sm`}>
                    {t.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-0.5 mb-1">
                      {[0,1,2,3,4].map(j => <span key={j} className="text-amber-400 text-[10px]">★</span>)}
                    </div>
                    <p className="text-white/85 text-xs italic leading-snug">"{t.q}"</p>
                    <p className="text-white/45 text-[10px] mt-1 font-bold">— {t.name} · {t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PremiumDarkCard>

        {/* Footer */}
        <div className="text-center pt-4 pb-8">
          <p className="text-[#1a1a1a]/40 text-xs">
            © 2025 PRSNL · Accompagnement restaurateurs
          </p>
        </div>
      </div>

      {/* Sabril Closer Bubble — AI sales agent (replaces old WhatsApp widget) */}
      <SabrilCloserBubble page="prsnl" />
    </div>
  );
};

export default ResultsPage;
